import React, { useState, useCallback } from 'react';
import ICAL from 'ical.js';
import { CalendarEvent } from '../../types/ics';
import { Upload, Calendar as CalendarIcon, Download, Eye } from 'lucide-react';
import { Calendar } from './Calendar';
import { generateCalendarEvents } from '../../utils/calendar'; // Assuming this exists as in CurrentPlans.tsx
import { getCurrentPlans } from '../../utils/storage/currentPlans'; // Assuming this exists

export const ICSViewer: React.FC = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);

  const parseICSFile = useCallback((icsData: string) => {
    const jcalData = ICAL.parse(icsData);
    const comp = new ICAL.Component(jcalData);
    const vevents = comp.getAllSubcomponents('vevent');

    const parsedEvents: CalendarEvent[] = vevents.map((vevent) => {
      const event = new ICAL.Event(vevent);
      return {
        summary: event.summary,
        description: event.description,
        location: event.location,
        startDate: event.startDate.toJSDate(),
        endDate: event.endDate.toJSDate(),
        uid: event.uid,
      };
    });

    setEvents(parsedEvents.sort((a, b) => a.startDate.getTime() - b.startDate.getTime()));
  }, []);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      parseICSFile(content);
    };
    reader.readAsText(file);
  }, [parseICSFile]);

  const handleGenerateCalendar = () => {
    try {
      const currentPlans = getCurrentPlans();
      const icsContent = generateCalendarEvents(currentPlans, {
        alerts: []
      });

      // Trigger file download
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'current-plans.ics';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to generate calendar:', error);
      alert('Failed to generate calendar. Please check your current plans data.');
    }
  };

  const handleViewCurrentPlan = () => {
    try {
      const currentPlans = getCurrentPlans();
      const icsContent = generateCalendarEvents(currentPlans);
      parseICSFile(icsContent); // Display events directly
    } catch (error) {
      console.error('Failed to view current plan:', error);
      alert('Failed to view current plan. Please check your current plans data.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center justify-center mb-4">
            <CalendarIcon className="w-12 h-12 text-blue-500 mr-4" />
            <h1 className="text-3xl font-bold text-gray-800">
              Current Plans Calendar <sup className="text-sm text-blue-600 font-medium">Beta</sup>
            </h1>
          </div>

          <p className="text-gray-600 text-center mb-6 max-w-2xl mx-auto">
            Upload your own calendar file, view your health plan in our online calendar, or download it as an ICS file compatible with Google and Apple calendars.
          </p>

          <div className="flex items-center justify-center gap-4">
            <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors cursor-pointer">
              <Upload className="w-5 h-5" />
              <span>Upload ICS</span>
              <input
                type="file"
                className="hidden"
                accept=".ics"
                onChange={handleFileUpload}
              />
            </label>

            <button
              onClick={handleGenerateCalendar}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
            >
              <Download className="w-5 h-5" />
              <span>Download Calendar</span>
            </button>

            <button
              onClick={handleViewCurrentPlan}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
            >
              <Eye className="w-5 h-5" />
              <span>View Current Plan</span>
            </button>
          </div>
        </div>

        {events.length > 0 ? (
          <Calendar events={events} />
        ) : (
          <div className="text-center text-gray-500 mt-8">
            No calendar events to display. Upload an ICS file or view current plans to get started.
          </div>
        )}
      </div>
    </div>
  );
};